import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { IonItemSliding, IonItem, IonItemOptions, IonItemOption } from '@ionic/react';
import { Icon } from '../../icons';
import { Badge, Btn, Card, MetricCard, SectionHeader, DataTable, FormRow, Field, Modal, FormActions, Pagination } from '../../components/ui';
import { getFestivals, createFestival, updateFestival, deleteFestival, getFestivalStats, getFestivalTransactions, createFestivalTransaction, updateFestivalTransaction, deleteFestivalTransaction, exportFestivalTransactions, downloadFestivalTemplate, importFestivalTransactions, downloadFestivalReceipt, downloadFestivalLedgerReport, downloadFestivalIncomeExpenseReport, downloadFestivalDonationReport, downloadFestivalExpenseCategoryReport } from '../../api/festival';
import { getErrorMessage, normalizePagination } from '../../utils/apiUtils';
import { getFile } from '../../api/files';
import { usePermissions } from '../../hooks/usePermissions';
import { formatDateByPreference } from '../../utils/formatters';
import { useSettings } from '../../context/SettingsContext';
import { toast } from 'react-hot-toast';
import en from '../../locales/en.json';
import hi from '../../locales/hi.json';
import gu from '../../locales/gu.json';

const translations = { en, hi, gu };

export default function FestivalScreen() {
  const { language = 'en' } = useSettings();
  const t = (key, params) => {
    let str = translations[language]?.festival?.[key] || translations.en?.festival?.[key] || key;
    if (params) { Object.keys(params).forEach(p => { str = str.replace(`{${p}}`, params[p]); }); }
    return str;
  };
  const { hasPermission } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [festivals, setFestivals] = useState([]);
  const [selectedFestivalId, setSelectedFestivalId] = useState("");
  const [stats, setStats] = useState({ total_income: 0, total_expenses: 0, net_surplus: 0, transaction_count: 0 });
  const [txns, setTxns] = useState([]);
  const [meta, setMeta] = useState(normalizePagination(null));
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({ type: '', category: '', payment_mode: '', date: '' });
  const [sort, setSort] = useState({ field: 'date', order: 'desc' });
  const [showFilters, setShowFilters] = useState(false);

  // Report Modal State
  const [reportModal, setReportModal] = useState({ open: false, tab: 'income' });
  const [reportsOpen, setReportsOpen] = useState(false);
  const reportsRef = useRef(null);

  useEffect(() => {
    const handler = e => { if (reportsRef.current && !reportsRef.current.contains(e.target)) setReportsOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Transaction Modal State
  const [modal, setModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const defaultForm = {
    type: "expense",
    amount: "",
    category: "Decoration",
    description: "",
    date: new Date().toISOString().split('T')[0],
    payment_mode: "Cash",
    is_recurring: false,
    recurring_frequency: "monthly",
    reference_number: "",
    status: "completed",
    payment_proof: null,
    existing_proof: null
  };
  const [form, setForm] = useState(defaultForm);
  const fileInputRef = useRef(null);

  const viewPaymentProof = async (f) => {
    const openUrl = (url) => {
      // Use anchor click for mobile browser compatibility (window.open blocked after async)
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    };

    try {
      if (f instanceof File) {
        openUrl(URL.createObjectURL(f));
        return;
      }
      // Use URL already set on the file object from the list API (signed URL)
      const cachedUrl = f?.temporary_url || f?.file_url;
      if (cachedUrl) {
        openUrl(cachedUrl);
        return;
      }
      // Fetch a fresh signed URL from the file API
      if (f?.id) {
        const res = await getFile(f.id);
        // axios interceptor unwraps: res = { success, data: fileObject, ... }
        const fileData = res?.data;
        const freshUrl = fileData?.temporary_url || fileData?.file_url;
        if (freshUrl) {
          openUrl(freshUrl);
          return;
        }
      }
      toast.error("Proof URL not available");
    } catch (err) {
      toast.error("Failed to open payment proof");
    }
  };

  // Festival Modal State
  const [festModal, setFestModal] = useState(false);
  const [festSaving, setFestSaving] = useState(false);
  const [editingFestId, setEditingFestId] = useState(null);
  const [festForm, setFestForm] = useState({ name: "", description: "", is_active: true });
  const [festErrors, setFestErrors] = useState({});
  const [deleteFestModal, setDeleteFestModal] = useState(false);
  const [deletingFest, setDeletingFest] = useState(false);

  const sf = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const fsf = k => e => {
    const val = e.target ? e.target.value : e;
    setFestForm(f => ({ ...f, [k]: val }));
    if (festErrors[k]) {
      setFestErrors(errs => ({ ...errs, [k]: null }));
    }
  };

  const fetchFestivals = async (selectLatest = true) => {
    try {
      const res = await getFestivals();
      const list = res.data || [];
      setFestivals(list);

      if (list.length > 0) {
        if (!selectedFestivalId || !list.find(f => f.id == selectedFestivalId)) {
          setSelectedFestivalId(list[0].id);
        }
      } else {
        setSelectedFestivalId("");
      }
    } catch (err) {
      console.error(getErrorMessage(err, t("toast_load_festivals_failed")));
    }
  };

  const fetchStatsAndTxns = useCallback(async () => {
    if (!selectedFestivalId) {
      setStats({ total_income: 0, total_expenses: 0, net_surplus: 0, transaction_count: 0 });
      setTxns([]);
      setMeta(normalizePagination(null));
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params = {
        page, per_page: 15,
        search: searchQuery,
        type: filters.type,
        category: filters.category,
        payment_mode: filters.payment_mode,
        date: filters.date,
        sort_by: sort.field,
        sort_order: sort.order
      };

      const [statsRes, txnsRes] = await Promise.all([
        getFestivalStats(selectedFestivalId),
        getFestivalTransactions(selectedFestivalId, params)
      ]);
      setStats(statsRes.data);
      setTxns(txnsRes.data || []);
      setMeta(normalizePagination(txnsRes.meta));
    } catch (err) {
      console.error(getErrorMessage(err, t("toast_load_festival_data_failed")));
    } finally {
      setLoading(false);
    }
  }, [selectedFestivalId, page, searchQuery, filters, sort]);

  const festFetched = useRef(false);
  useEffect(() => {
    if (festFetched.current) return;
    festFetched.current = true;
    fetchFestivals();
  }, []);

  const lastFetchParams = useRef(null);
  useEffect(() => {
    const currentParams = JSON.stringify({ selectedFestivalId, page, searchQuery, filters, sort });
    if (lastFetchParams.current === currentParams) return;
    lastFetchParams.current = currentParams;
    fetchStatsAndTxns();
  }, [fetchStatsAndTxns, selectedFestivalId, page, searchQuery, filters, sort]);

  const handleSave = async () => {
    if (parseFloat(form.amount) >= 5000 && !form.payment_proof && !editingId && !form.existing_proof) {
      toast.error("A payment proof is required for transactions of ₹5,000 or above.");
      return;
    }
    setSaving(true);
    try {
      const payload = new FormData();
      Object.keys(form).forEach(key => {
        if (key === 'existing_proof') return;
        if (key === 'payment_proof') {
          if (form[key] instanceof File) {
            payload.append('payment_proof', form[key]);
          }
        } else if (key === 'is_recurring') {
          payload.append('is_recurring', form.is_recurring ? '1' : '0');
        } else if (form[key] !== null && form[key] !== undefined) {
          payload.append(key, form[key]);
        }
      });

      if (editingId) {
        await updateFestivalTransaction(editingId, payload);
      } else {
        await createFestivalTransaction(selectedFestivalId, payload);
      }
      setModal(false);
      fetchStatsAndTxns();
      setForm(defaultForm);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setEditingId(null);
    } catch (err) {
      toast.error(getErrorMessage(err, t("toast_save_entry_failed")));
    } finally {
      setSaving(false);
    }
  };

  const validateFestForm = () => {
    const errors = {};
    const nameTrim = festForm.name?.trim();
    if (!nameTrim) {
      errors.name = t("val_festival_name_required");
    } else if (nameTrim.length < 2) {
      errors.name = t("val_festival_name_min");
    } else if (nameTrim.length > 255) {
      errors.name = t("val_festival_name_max");
    } else {
      const isDuplicate = festivals.some(
        f => f.name?.trim().toLowerCase() === nameTrim.toLowerCase() && f.id != editingFestId
      );
      if (isDuplicate) {
        errors.name = t("val_festival_name_exists");
      }
    }

    if (festForm.description && festForm.description.length > 1000) {
      errors.description = t("val_description_max");
    }

    setFestErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error(t("toast_form_validation_failed") || "Please check the form for errors");
      return false;
    }
    return true;
  };

  const handleSaveFestival = async () => {
    if (!validateFestForm()) return;
    setFestSaving(true);
    try {
      const payload = {
        name: festForm.name.trim(),
        description: festForm.description ? festForm.description.trim() : null,
        is_active: !!festForm.is_active,
      };
      if (editingFestId) {
        await updateFestival(editingFestId, payload);
      } else {
        const res = await createFestival(payload);
        setSelectedFestivalId(res.data.id);
      }
      setFestModal(false);
      setFestForm({ name: "", description: "", is_active: true });
      setFestErrors({});
      setEditingFestId(null);
      await fetchFestivals(false);
      setPage(1);
    } catch (err) {
      if (err.response?.data?.errors) {
        const backendErrs = {};
        Object.keys(err.response.data.errors).forEach(k => {
          backendErrs[k] = Array.isArray(err.response.data.errors[k])
            ? err.response.data.errors[k][0]
            : err.response.data.errors[k];
        });
        setFestErrors(backendErrs);
        toast.error(t("toast_form_validation_failed") || "Please check the form for errors");
      } else {
        toast.error(getErrorMessage(err, t("toast_save_festival_failed")));
      }
    } finally {
      setFestSaving(false);
    }
  };

  const handleEditFest = () => {
    const fest = festivals.find(f => f.id == selectedFestivalId);
    if (!fest) return;
    setFestForm({ name: fest.name, description: fest.description || "", is_active: fest.is_active ?? true });
    setEditingFestId(fest.id);
    setFestErrors({});
    setFestModal(true);
  };

  const handleDeleteFest = () => {
    setDeleteFestModal(true);
  };

  const confirmDeleteFest = async () => {
    if (!selectedFestivalId) return;
    setDeletingFest(true);
    try {
      await deleteFestival(selectedFestivalId);
      toast.success(t("toast_delete_festival_success") || "Festival deleted successfully");
      const remaining = festivals.filter(f => f.id != selectedFestivalId);
      if (remaining.length > 0) {
        setSelectedFestivalId(remaining[0].id);
      } else {
        setSelectedFestivalId("");
      }
      setDeleteFestModal(false);
      await fetchFestivals(false);
    } catch (err) {
      toast.error(getErrorMessage(err, t("toast_delete_festival_failed")));
    } finally {
      setDeletingFest(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("confirm_delete_transaction"))) return;
    try {
      await deleteFestivalTransaction(id);
      fetchStatsAndTxns();
    } catch (err) {
      toast.error(getErrorMessage(err, t("toast_delete_entry_failed")));
    }
  };

  const handleEdit = (txn) => {
    setForm({
      type: txn.type,
      amount: txn.amount,
      category: txn.category,
      description: txn.description,
      date: txn.date,
      payment_mode: txn.payment_mode,
      is_recurring: !!txn.is_recurring,
      recurring_frequency: txn.recurring_frequency || 'monthly',
      reference_number: txn.reference_number || '',
      status: txn.status || 'completed',
      payment_proof: null,
      existing_proof: txn.file || null
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
    setEditingId(txn.id);
    setModal(true);
  };

  const handleDuplicate = (txn) => {
    setForm({
      type: txn.type,
      amount: txn.amount,
      category: txn.category,
      description: txn.description,
      date: new Date().toISOString().split('T')[0],
      payment_mode: txn.payment_mode,
      is_recurring: !!txn.is_recurring,
      recurring_frequency: txn.recurring_frequency || 'monthly',
      reference_number: '', // Don't duplicate reference number
      status: 'completed',
      payment_proof: null,
      existing_proof: null
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
    setEditingId(null);
    setModal(true);
  };

  const [viewTxn, setViewTxn] = useState(null);

  const handleDownloadReceipt = async (txnOrId) => {
    const toastId = toast.loading("Downloading receipt...");
    try {
      const txnObj = typeof txnOrId === 'object' && txnOrId !== null
        ? txnOrId
        : (viewTxn && (viewTxn.id === txnOrId || viewTxn.txn_id === txnOrId)
            ? viewTxn
            : txns.find(t => t.id === txnOrId || t.txn_id === txnOrId) || {});

      const festivalId = txnObj.festival_id || selectedFestivalId;
      const transactionId = typeof txnOrId === 'object' && txnOrId !== null ? txnOrId.id : (txnObj.id || txnOrId);

      if (!festivalId || !transactionId) {
        toast.error("Invalid transaction selected", { id: toastId });
        return;
      }

      const res = await downloadFestivalReceipt(festivalId, transactionId);
      const blob = res instanceof Blob ? res : new Blob([res], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const filename = txnObj.txn_id ? `receipt_${txnObj.txn_id}.pdf` : `receipt_txn_${transactionId}.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Receipt downloaded successfully", { id: toastId });
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to download receipt"), { id: toastId });
    }
  };

  const handleDownloadReport = async (type) => {
    if (!selectedFestivalId) return;
    const toastId = toast.loading("Generating report...");
    try {
      let res;
      if (type === 'ledger') res = await downloadFestivalLedgerReport(selectedFestivalId);
      else if (type === 'income-expense') res = await downloadFestivalIncomeExpenseReport(selectedFestivalId);
      else if (type === 'donation') res = await downloadFestivalDonationReport(selectedFestivalId);
      else if (type === 'category') res = await downloadFestivalExpenseCategoryReport(selectedFestivalId);
      
      const url = window.URL.createObjectURL(new Blob([res]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `festival_${type}_report.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      toast.success("Report downloaded successfully", { id: toastId });
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to download report"), { id: toastId });
    }
  };

  const handleExport = async () => {
    if (!selectedFestivalId) return;
    try {
      const params = { search: searchQuery, type: filters.type, category: filters.category, payment_mode: filters.payment_mode, date: filters.date, sort_by: sort.field, sort_order: sort.order };
      const res = await exportFestivalTransactions(selectedFestivalId, params);
      const url = window.URL.createObjectURL(new Blob([res]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `festival_${selectedFestivalId}_transactions.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (err) {
      toast.error(t("Failed to export transactions"));
    }
  };

  const [importModal, setImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);

  const handleDownloadTemplate = async () => {
    try {
      const res = await downloadFestivalTemplate();
      const url = window.URL.createObjectURL(new Blob([res]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `festival_transactions_template.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (err) {
      toast.error(t("Failed to download template"));
    }
  };

  const handleImport = async () => {
    if (!importFile) return toast.error(t("Please select a file to import"));
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      await importFestivalTransactions(selectedFestivalId, formData);
      toast.success(t("Transactions imported successfully"));
      setImportModal(false);
      setImportFile(null);
      fetchStatsAndTxns();
    } catch (err) {
      toast.error(getErrorMessage(err, t("Failed to import transactions")));
    } finally {
      setImporting(false);
    }
  };

  const handleCloseModal = () => {
    setModal(false);
    setForm(defaultForm);
    if (fileInputRef.current) fileInputRef.current.value = "";
    setEditingId(null);
  };

  const handleCloseFestModal = () => {
    setFestModal(false);
    setFestForm({ name: "", description: "", is_active: true });
    setFestErrors({});
    setEditingFestId(null);
  };

  const currentFestival = festivals.find(f => f.id == selectedFestivalId);

  return (
    <div className="fade-up space-y-4">
      {/* Transaction Modal */}
      {modal && (
        <Modal title={editingId ? t("title_edit_transaction") : t("title_add_transaction")} onClose={handleCloseModal}>
          <div onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}>
            <FormRow cols={2}>
              <Field label={t("label_type")} type="select" value={form.type} onChange={sf("type")} options={[{ label: t("opt_income"), value: "income" }, { label: t("opt_expense"), value: "expense" }]} />
              <Field label={t("label_amount_rs")} type="number" value={form.amount} onChange={sf("amount")} required />
            </FormRow>
            <FormRow cols={2}>
              <Field label={t("label_category")} type="select" value={form.category} onChange={sf("category")} options={form.type === "income" ? [t("cat_sponsorship"), t("cat_donations"), t("cat_entry_fees"), t("cat_misc")] : [t("cat_decoration"), t("cat_food"), t("cat_entertainment"), t("cat_security"), t("cat_misc")]} />
              <Field label={t("label_date")} type="date" value={form.date} onChange={sf("date")} />
            </FormRow>
            <FormRow cols={2}>
              <Field label={t("label_payment_mode")} type="select" value={form.payment_mode} onChange={sf("payment_mode")} options={[t("mode_cash"), t("mode_upi"), t("mode_bank_transfer"), t("mode_cheque")]} />
              <Field label="Status" type="select" value={form.status} onChange={sf("status")} options={[{ label: 'Completed', value: 'completed' }, { label: 'Pending', value: 'pending' }]} />
            </FormRow>
            <FormRow cols={2}>
              <Field label="Reference Number" placeholder="Txn ID, Cheque No, etc." value={form.reference_number} onChange={sf("reference_number")} />
              <div>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                  Payment Proof (Receipt, Image)
                  {parseFloat(form.amount || 0) >= 5000 && <span className="text-red-500 ml-1">*</span>}
                </label>

                {/* Existing Payment Proof when editing */}
                {form.existing_proof && !form.payment_proof && (
                  <div className="flex items-center justify-between p-2 mb-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60">
                    <div className="flex items-center gap-2 min-w-0 flex-1 mr-2">
                      <Icon name="document" size={16} className="text-primary flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-medium text-slate-800 dark:text-slate-200 truncate">
                          {form.existing_proof.original_name || "Attached Receipt"}
                        </div>
                        <div className="text-[10px] text-slate-400">Current Proof</div>
                      </div>
                    </div>
                    <Btn
                      type="button"
                      variant="outline"
                      xs
                      icon="eye"
                      onClick={() => viewPaymentProof(form.existing_proof)}
                      title="View Attached Proof"
                    >
                      View
                    </Btn>
                  </div>
                )}

                {/* Newly Selected File Preview */}
                {form.payment_proof instanceof File && (
                  <div className="flex items-center justify-between p-2 mb-2 rounded-lg border border-primary/30 bg-primary/5 dark:bg-primary/10">
                    <div className="flex items-center gap-2 min-w-0 flex-1 mr-2">
                      <Icon name={form.payment_proof.type === 'application/pdf' ? "document" : "image"} size={16} className="text-primary flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-medium text-slate-800 dark:text-slate-200 truncate">
                          {form.payment_proof.name}
                        </div>
                        <div className="text-[10px] text-slate-400">
                          {(form.payment_proof.size / 1024).toFixed(1)} KB (New upload)
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Btn
                        type="button"
                        variant="outline"
                        xs
                        icon="eye"
                        onClick={() => viewPaymentProof(form.payment_proof)}
                        title="View Preview"
                      >
                        View
                      </Btn>
                      <button
                        type="button"
                        className="p-1 text-slate-400 hover:text-red-500 rounded transition-colors text-xs font-bold leading-none cursor-pointer"
                        onClick={() => {
                          setForm(f => ({ ...f, payment_proof: null }));
                          if (fileInputRef.current) fileInputRef.current.value = "";
                        }}
                        title="Remove"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                )}

                <input 
                  ref={fileInputRef}
                  type="file" 
                  accept="image/*,.pdf" 
                  onChange={(e) => setForm(f => ({ ...f, payment_proof: e.target.files[0] || null }))} 
                  className="w-full text-sm text-slate-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-primary/10 file:text-primary hover:file:bg-primary/20" 
                />
                {form.existing_proof && !form.payment_proof && (
                  <p className="text-[11px] text-slate-400 mt-1">Select a new file above to replace the current proof.</p>
                )}
              </div>
            </FormRow>
            <FormRow>
              <div className="flex flex-col gap-3 mt-2">
                <label className="flex items-center gap-2 cursor-pointer w-fit">
                  <input
                    type="checkbox"
                    checked={form.is_recurring}
                    onChange={(e) => setForm(f => ({ ...f, is_recurring: e.target.checked }))}
                    className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary/20 dark:border-slate-600 dark:bg-slate-700"
                  />
                  <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Set as recurring transaction</span>
                </label>
                {form.is_recurring && (
                  <div className="w-1/2">
                    <Field
                      label="Frequency"
                      type="select"
                      value={form.recurring_frequency}
                      onChange={sf("recurring_frequency")}
                      options={[
                        { label: "Daily", value: "daily" },
                        { label: "Weekly", value: "weekly" },
                        { label: "Monthly", value: "monthly" },
                        { label: "Yearly", value: "yearly" }
                      ]}
                    />
                  </div>
                )}
              </div>
            </FormRow>
            <FormRow><Field label={t("label_description")} type="textarea" value={form.description} onChange={sf("description")} rows={2} /></FormRow>
            <FormActions onSave={handleSave} onCancel={handleCloseModal} label={editingId ? t("btn_update_entry") : t("btn_add_entry")} loading={saving} />
          </div>
        </Modal>
      )}

      {/* Festival Modal */}
      {festModal && (
        <Modal title={editingFestId ? (t("title_edit_festival") === "title_edit_festival" ? "Edit Festival" : t("title_edit_festival")) : (t("title_create_festival") === "title_create_festival" ? "Create Festival" : t("title_create_festival"))} onClose={handleCloseFestModal}>
          <div onKeyDown={(e) => { if (e.key === 'Enter') handleSaveFestival(); }}>
            <FormRow>
              <Field 
                label={t("label_festival_name")} 
                placeholder={t("placeholder_festival_name") === "placeholder_festival_name" ? "e.g. Navratri 2026, Sports Day" : t("placeholder_festival_name")} 
                value={festForm.name} 
                onChange={fsf("name")} 
                error={festErrors.name}
                maxLength={255}
                required 
              />
            </FormRow>
            <FormRow>
              <Field 
                label={t("label_description")} 
                type="textarea" 
                placeholder={t("placeholder_event_description") === "placeholder_event_description" ? "Brief about the event..." : t("placeholder_event_description")} 
                value={festForm.description} 
                onChange={fsf("description")} 
                error={festErrors.description}
                maxLength={1000}
                rows={3} 
              />
            </FormRow>
            <FormRow>
              <Field 
                label="Status" 
                type="select" 
                value={festForm.is_active ? "1" : "0"} 
                onChange={(e) => setFestForm(f => ({ ...f, is_active: e.target.value === "1" }))} 
                options={[{ label: "Active", value: "1" }, { label: "Completed", value: "0" }]} 
              />
            </FormRow>
            <FormActions onSave={handleSaveFestival} onCancel={handleCloseFestModal} label={editingFestId ? (t("btn_update_festival") === "btn_update_festival" ? "Update Festival" : t("btn_update_festival")) : (t("btn_create_festival") === "btn_create_festival" ? "Create Festival" : t("btn_create_festival"))} loading={festSaving} />
          </div>
        </Modal>
      )}

      {/* Delete Festival Modal */}
      {deleteFestModal && (
        <Modal 
          title={t("title_delete_festival") || "Delete Festival Account"} 
          onClose={() => !deletingFest && setDeleteFestModal(false)}
        >
          <div className="space-y-4">
            <div className="flex items-start gap-3 rounded-xl border border-red-200/80 bg-red-50/70 p-3.5 dark:border-red-900/50 dark:bg-red-950/30">
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400">
                <Icon name="trash" size={18} />
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-bold text-red-900 dark:text-red-200">
                  {t("heading_delete_festival") || "Delete Festival Account"}
                </h4>
                <p className="text-xs text-red-600 dark:text-red-400 mt-0.5 leading-relaxed">
                  {t("confirm_delete_festival") || "Are you sure? This will delete the entire festival and ALL its transactions forever."}
                </p>
              </div>
            </div>

            {currentFestival && (
              <div className="rounded-xl border border-border dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 p-4 space-y-2.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted font-medium">{t("label_festival_name") || "Festival Name"}</span>
                  <span className="text-sm font-bold text-ink dark:text-slate-100">{currentFestival.name}</span>
                </div>
                {currentFestival.description && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted font-medium">{t("label_description") || "Description"}</span>
                    <span className="text-xs text-ink dark:text-slate-200 max-w-[240px] truncate">{currentFestival.description}</span>
                  </div>
                )}
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted font-medium">Status</span>
                  <Badge color={currentFestival.is_active ? "green" : "slate"} sm>
                    {currentFestival.is_active ? "Active" : "Completed"}
                  </Badge>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-3 border-t border-border dark:border-slate-800">
              <Btn 
                variant="default" 
                onClick={() => setDeleteFestModal(false)} 
                disabled={deletingFest}
              >
                {t("btn_cancel") || "Cancel"}
              </Btn>
              <Btn 
                variant="solid_danger" 
                onClick={confirmDeleteFest} 
                loading={deletingFest} 
                icon="trash"
              >
                {t("btn_delete") || "Delete"}
              </Btn>
            </div>
          </div>
        </Modal>
      )}

      {/* Import Modal */}
      {importModal && (
        <Modal title={t("title_import_transactions", { default: "Import Transactions" })} onClose={() => setImportModal(false)}>
          <div className="space-y-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl border border-blue-100 dark:border-blue-800/50">
              <h4 className="font-semibold text-blue-800 dark:text-blue-300 mb-2">Instructions</h4>
              <ol className="list-decimal list-inside text-sm text-blue-700 dark:text-blue-400 space-y-1">
                <li>Download the template file below.</li>
                <li>Fill in the transactions following the column headers.</li>
                <li>Save and upload the file back here.</li>
              </ol>
              <div className="mt-3">
                <Btn variant="outline" size="sm" icon="download" onClick={handleDownloadTemplate}>Download Template</Btn>
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">Upload Filled Template</label>
              <input type="file" accept=".xlsx,.xls,.csv" onChange={(e) => setImportFile(e.target.files[0])} className="w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary/10 file:text-primary hover:file:bg-primary/20" />
            </div>
            <FormActions onSave={handleImport} onCancel={() => setImportModal(false)} label="Import" loading={importing} />
          </div>
        </Modal>
      )}

      {/* View Transaction Modal */}
      {viewTxn && (
        <Modal title={t("title_view_transaction", { default: "Transaction Details" })} onClose={() => setViewTxn(null)}>
          <div className="space-y-4">
            <div className="flex justify-between items-start border-b border-slate-100 dark:border-slate-700 pb-4">
              <div>
                <div className="text-sm text-slate-500">Transaction ID</div>
                <div className="font-bold text-slate-800 dark:text-slate-100">{viewTxn.txn_id || '-'}</div>
              </div>
              <div className="text-right">
                <div className="text-sm text-slate-500">Amount</div>
                <div className={`text-xl font-black ${viewTxn.type === 'income' ? 'text-emerald-600' : 'text-red-600'}`}>
                  {viewTxn.type === 'income' ? '+' : '-'}₹{Number(viewTxn.amount).toLocaleString()}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 border-b border-slate-100 dark:border-slate-700 pb-4">
              <div>
                <div className="text-sm text-slate-500">Date</div>
                <div className="font-semibold">{formatDateByPreference(viewTxn.date)}</div>
              </div>
              <div>
                <div className="text-sm text-slate-500">Type & Category</div>
                <div className="font-semibold capitalize">{viewTxn.type} - {viewTxn.category}</div>
              </div>
              <div>
                <div className="text-sm text-slate-500">Payment Mode</div>
                <div className="font-semibold">{viewTxn.payment_mode}</div>
              </div>
              <div>
                <div className="text-sm text-slate-500">Status</div>
                <div className="font-semibold capitalize">{viewTxn.status}</div>
              </div>
              {viewTxn.reference_number && (
                <div className="col-span-2">
                  <div className="text-sm text-slate-500">Reference Number</div>
                  <div className="font-semibold">{viewTxn.reference_number}</div>
                </div>
              )}
            </div>
            {viewTxn.description && (
              <div className="border-b border-slate-100 dark:border-slate-700 pb-4">
                <div className="text-sm text-slate-500">Description</div>
                <div className="text-slate-700 dark:text-slate-300">{viewTxn.description}</div>
              </div>
            )}
            <div className="grid grid-cols-1 gap-2 pt-3 border-t border-slate-100 dark:border-slate-700 sm:flex sm:flex-row sm:justify-end sm:gap-2">
              {viewTxn.file && (
                <Btn
                  variant="secondary"
                  icon="link"
                  onClick={() => viewPaymentProof(viewTxn.file)}
                  full
                >
                  View Proof
                </Btn>
              )}
              <Btn variant="primary" icon="download" onClick={() => handleDownloadReceipt(viewTxn)} full>Download PDF</Btn>
              <Btn variant="default" icon="close" onClick={() => setViewTxn(null)} full>Close</Btn>
            </div>
          </div>
        </Modal>
      )}

      {/* Report Modal */}
      {reportModal.open && (
        <Modal title={t("title_festival_report", { default: "Festival Report" })} onClose={() => setReportModal({ open: false, tab: reportModal.tab })} maxWidth="max-w-2xl">
          <div className="mb-4 flex items-center gap-2 border-b border-slate-200 dark:border-slate-700 pb-2 overflow-x-auto">
            {['income', 'expenses', 'surplus', 'transactions'].map(tab => (
              <button
                key={tab}
                onClick={() => setReportModal({ ...reportModal, tab })}
                className={`px-4 py-2 rounded-lg text-sm font-bold whitespace-nowrap transition-colors ${reportModal.tab === tab ? 'bg-primary text-white shadow-md' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
              >
                {t(`tab_${tab}`, { default: tab.charAt(0).toUpperCase() + tab.slice(1) })}
              </button>
            ))}
          </div>

          <div className="py-2 min-h-[300px]">
            {reportModal.tab === 'income' && (
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">{t("title_income_breakdown", { default: "Income Breakdown" })}</h3>
                <div className="p-6 text-center rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700">
                  <div className="text-sm font-bold text-emerald-600 dark:text-emerald-400 mb-1">{t("card_total_income", { default: "Total Income" })}</div>
                  <div className="text-4xl font-black text-emerald-700 dark:text-emerald-300">₹{(stats.total_income || 0).toLocaleString()}</div>
                </div>
                <div className="text-sm text-slate-500 text-center mt-4">
                  {t("desc_view_ledger_below", { default: "View the full detailed ledger below." })}
                </div>
              </div>
            )}

            {reportModal.tab === 'expenses' && (
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">{t("title_expense_breakdown", { default: "Expense Breakdown" })}</h3>
                <div className="p-6 text-center rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700">
                  <div className="text-sm font-bold text-red-600 dark:text-red-400 mb-1">{t("card_total_expenses", { default: "Total Expenses" })}</div>
                  <div className="text-4xl font-black text-red-700 dark:text-red-300">₹{(stats.total_expenses || 0).toLocaleString()}</div>
                </div>
                <div className="text-sm text-slate-500 text-center mt-4">
                  {t("desc_view_ledger_below", { default: "View the full detailed ledger below." })}
                </div>
              </div>
            )}

            {reportModal.tab === 'surplus' && (
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">{t("title_surplus_deficit", { default: "Surplus / Deficit" })}</h3>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="p-4 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800/50 text-center">
                    <div className="text-sm font-bold text-emerald-600 dark:text-emerald-400 mb-1">{t("card_total_income", { default: "Total Income" })}</div>
                    <div className="text-2xl font-black text-emerald-700 dark:text-emerald-300">₹{(stats.total_income || 0).toLocaleString()}</div>
                  </div>
                  <div className="p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/50 text-center">
                    <div className="text-sm font-bold text-red-600 dark:text-red-400 mb-1">{t("card_total_expenses", { default: "Total Expenses" })}</div>
                    <div className="text-2xl font-black text-red-700 dark:text-red-300">₹{(stats.total_expenses || 0).toLocaleString()}</div>
                  </div>
                </div>
                <div className={`p-5 rounded-xl border ${stats.net_surplus >= 0 ? 'bg-blue-50 border-blue-100 dark:bg-blue-900/20 dark:border-blue-800/50' : 'bg-orange-50 border-orange-100 dark:bg-orange-900/20 dark:border-orange-800/50'}`}>
                  <div className={`text-sm font-bold mb-1 ${stats.net_surplus >= 0 ? 'text-blue-600 dark:text-blue-400' : 'text-orange-600 dark:text-orange-400'}`}>{t("card_net_surplus", { default: "Net Surplus" })}</div>
                  <div className={`text-3xl font-black ${stats.net_surplus >= 0 ? 'text-blue-700 dark:text-blue-300' : 'text-orange-700 dark:text-orange-300'}`}>
                    {stats.net_surplus >= 0 ? '+' : '-'}₹{Math.abs(stats.net_surplus || 0).toLocaleString()}
                  </div>
                  <div className="mt-2 text-sm text-slate-500">
                    {stats.net_surplus >= 0
                      ? t("desc_surplus_positive", { default: "The festival generated a positive surplus." })
                      : t("desc_surplus_negative", { default: "The festival incurred a net deficit." })}
                  </div>
                </div>
              </div>
            )}

            {reportModal.tab === 'transactions' && (
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">{t("title_transaction_stats", { default: "Transaction Statistics" })}</h3>
                <div className="p-6 text-center rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700">
                  <div className="text-5xl font-black text-primary mb-2">{stats.transaction_count}</div>
                  <div className="text-slate-500 font-medium">{t("desc_total_transactions_recorded", { default: "Total transactions recorded for this event." })}</div>
                </div>
                <div className="text-sm text-slate-500 text-center mt-4">
                  {t("desc_view_ledger_below", { default: "View the full detailed ledger below." })}
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
          <div className="flex items-center bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl px-2 py-1.5 shadow-inner min-w-0 max-w-full overflow-hidden">
            <span className="text-sm font-bold text-slate-400 px-2 uppercase tracking-wider border-r border-slate-200 mr-1 flex-shrink-0">{t("label_event")}</span>
            <select
              value={selectedFestivalId}
              onChange={e => { setSelectedFestivalId(e.target.value); setPage(1); }}
              className="bg-white dark:bg-slate-800 border-0 text-sm font-bold text-slate-700 dark:text-slate-300 outline-none py-0 pl-1 pr-6 focus:ring-0 cursor-pointer min-w-0 w-[110px] sm:w-[180px] truncate"
            >
              {festivals.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
            {selectedFestivalId && (
              <div className="flex items-center gap-1.5 ml-1 pl-2 border-l border-slate-200 flex-shrink-0">
                <Badge color={festivals.find(f => f.id == selectedFestivalId)?.is_active ? 'green' : 'slate'} size="sm" className="hidden sm:flex uppercase">
                  {festivals.find(f => f.id == selectedFestivalId)?.is_active ? 'Active' : 'Completed'}
                </Badge>
                <div className="flex items-center gap-1">
                  <Btn sm variant="default" icon={"edit"} onClick={handleEditFest} />
                  <Btn sm variant="danger" icon={"trash"} onClick={handleDeleteFest} />
                </div>
              </div>
            )}
          </div>
          {hasPermission('festivals.create') && (
            <Btn variant="default" icon="plus" onClick={() => { setEditingFestId(null); setFestForm({ name: "", description: "", is_active: true }); setFestErrors({}); setFestModal(true); }}>{t("btn_new_event")}</Btn>
          )}
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="relative" ref={reportsRef}>
            <Btn variant="outline" icon="description" className="hidden lg:flex" onClick={() => setReportsOpen(r => !r)} disabled={!selectedFestivalId}>Reports</Btn>
            {reportsOpen && (
              <div className="dropdown-menu absolute right-0 w-64 mt-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg z-[100] py-1">
                <div className="px-3 py-2 text-xs font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100 dark:border-slate-700">PDF Reports</div>
                <button onClick={() => { setReportsOpen(false); handleDownloadReport('ledger'); }} className="w-full text-left px-4 py-2 hover:bg-slate-50 dark:hover:bg-slate-700/50 text-sm font-medium text-slate-700 dark:text-slate-300 border-0 bg-transparent cursor-pointer">Download Ledger PDF</button>
                <button onClick={() => { setReportsOpen(false); handleDownloadReport('income-expense'); }} className="w-full text-left px-4 py-2 hover:bg-slate-50 dark:hover:bg-slate-700/50 text-sm font-medium text-slate-700 dark:text-slate-300 border-0 bg-transparent cursor-pointer">Income & Expense Report</button>
                <button onClick={() => { setReportsOpen(false); handleDownloadReport('donation'); }} className="w-full text-left px-4 py-2 hover:bg-slate-50 dark:hover:bg-slate-700/50 text-sm font-medium text-slate-700 dark:text-slate-300 border-0 bg-transparent cursor-pointer">Donation Summary</button>
                <button onClick={() => { setReportsOpen(false); handleDownloadReport('category'); }} className="w-full text-left px-4 py-2 hover:bg-slate-50 dark:hover:bg-slate-700/50 text-sm font-medium text-slate-700 dark:text-slate-300 border-0 bg-transparent cursor-pointer">Expense Category Report</button>
              </div>
            )}
          </div>
          <Btn variant="outline" icon="upload" className="hidden lg:flex" onClick={() => setImportModal(true)} disabled={!selectedFestivalId}>{t("btn_import", { default: "Import" })}</Btn>
          <Btn variant="outline" icon="download" className="hidden lg:flex" onClick={handleExport} disabled={!selectedFestivalId}>{t("btn_export", { default: "Export" })}</Btn>
          {hasPermission('festivals.create') && (
            <Btn variant="primary" icon="plus" className="hidden lg:flex w-full sm:w-auto" onClick={() => { setEditingId(null); setModal(true); }} disabled={!selectedFestivalId}>{t("btn_add_entry")}</Btn>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricCard label={t("card_total_income")} value={`₹${(stats.total_income || 0).toLocaleString()}`} delta={t("desc_all_sources")} deltaUp icon="trend_up" accent="green" onClick={() => setReportModal({ open: true, tab: 'income' })} />
        <MetricCard label={t("card_total_expenses")} value={`₹${(stats.total_expenses || 0).toLocaleString()}`} delta={t("desc_all_spending")} deltaUp={false} icon="trend_down" accent="red" onClick={() => setReportModal({ open: true, tab: 'expenses' })} />
        <MetricCard label={t("card_net_surplus")} value={`₹${(stats.net_surplus || 0).toLocaleString()}`} delta={t("desc_after_expenses")} deltaUp={(stats.net_surplus || 0) >= 0} icon="money" accent="blue" onClick={() => setReportModal({ open: true, tab: 'surplus' })} />
        <MetricCard label={t("card_transactions")} value={stats.transaction_count} icon="activity" accent="purple" onClick={() => setReportModal({ open: true, tab: 'transactions' })} />
      </div>

      <Card>
        <SectionHeader
          title={
            <span className="block truncate max-w-[240px] sm:max-w-none">
              {t("title_full_ledger", { name: festivals.find(f => String(f.id) === String(selectedFestivalId))?.name || 'Festival' })}
            </span>
          }
          subtitle={t("subtitle_full_ledger")}
          className="flex-col sm:flex-row sm:items-center gap-3"
          actionClassName="w-full sm:w-auto"
          action={
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <div className="relative flex-1 sm:w-64">
                <div className="absolute left-3 top-2.5 text-slate-400"><Icon name="search" size={18} /></div>
                <input
                  type="text"
                  placeholder="Search description..."
                  className="w-full pl-9 pr-4 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all"
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                />
              </div>
              <Btn variant={showFilters ? "primary" : "outline"} icon="tune" onClick={() => setShowFilters(!showFilters)}>
                Filters {Object.values(filters).some(x => x) ? <span className="w-2 h-2 rounded-full bg-red-500 absolute top-1 right-1"></span> : null}
              </Btn>
            </div>
          }
        />

        {showFilters && (
          <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl border border-slate-100 dark:border-slate-700 mb-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
            <Field label="Type" type="select" value={filters.type} onChange={e => { setFilters(f => ({ ...f, type: e.target.value })); setPage(1); }} options={[{ label: 'All', value: '' }, { label: 'Income', value: 'income' }, { label: 'Expense', value: 'expense' }]} />
            <Field label="Category" type="select" value={filters.category} onChange={e => { setFilters(f => ({ ...f, category: e.target.value })); setPage(1); }} options={[{ label: 'All', value: '' }, { label: t("cat_decoration"), value: "Decoration" }, { label: t("cat_food"), value: "Food" }, { label: t("cat_entertainment"), value: "Entertainment" }, { label: t("cat_security"), value: "Security" }, { label: t("cat_donations"), value: "Donations" }, { label: t("cat_sponsorship"), value: "Sponsorship" }, { label: t("cat_entry_fees"), value: "Entry Fees" }, { label: t("cat_misc"), value: "Misc" }]} />
            <Field label="Payment Mode" type="select" value={filters.payment_mode} onChange={e => { setFilters(f => ({ ...f, payment_mode: e.target.value })); setPage(1); }} options={[{ label: 'All', value: '' }, { label: t("mode_cash"), value: "Cash" }, { label: t("mode_upi"), value: "UPI" }, { label: t("mode_cheque"), value: "Cheque" }, { label: t("mode_bank_transfer"), value: "Bank Transfer" }]} />
            <Field label="Sort By" type="select" value={`${sort.field}-${sort.order}`} onChange={e => { const [f, o] = e.target.value.split('-'); setSort({ field: f, order: o }); setPage(1); }} options={[
              { label: 'Date (Newest)', value: 'date-desc' },
              { label: 'Date (Oldest)', value: 'date-asc' },
              { label: 'Amount (High to Low)', value: 'amount-desc' },
              { label: 'Amount (Low to High)', value: 'amount-asc' },
              { label: 'Type (A-Z)', value: 'type-asc' }
            ]} />
            <Field label="Date" type="date" value={filters.date} onChange={e => { setFilters(f => ({ ...f, date: e.target.value })); setPage(1); }} />
            <div className="flex items-end sm:col-span-2 md:col-span-5 mt-2">
              <Btn variant="outline" className="w-full sm:w-auto" onClick={() => { setFilters({ type: '', category: '', payment_mode: '', date: '' }); setSearchQuery(""); setSort({ field: 'date', order: 'desc' }); setPage(1); }}>Clear Filters</Btn>
            </div>
          </div>
        )}

        {(() => {
          const showBalance = !searchQuery && !filters.type && !filters.category && !filters.payment_mode && !filters.date && sort.field === 'date';
          const cols = ["Txn ID", t("label_date"), t("label_type"), t("label_category"), t("label_description"), t("label_amount_rs"), t("col_mode"), "Status"];
          if (showBalance) cols.push("Balance (₹)");
          cols.push(t("col_actions"));

          return (
            <div className="hidden lg:block">
              <DataTable
                cols={cols}
                loading={loading}
                rows={txns.map(txn => {
                  const row = [
                    <span className="text-xs font-mono text-slate-500 bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">{txn.txn_id || '-'}</span>,
                    formatDateByPreference(txn.date),
                    <Badge color={txn.type === "income" ? "green" : "red"} dot className="capitalize">
                      {txn.type === 'income' ? t("opt_income") : t("opt_expense")}
                    </Badge>,
                    txn.category,
                    <span className="text-slate-600 dark:text-slate-400 truncate max-w-[200px]" title={txn.description}>
                      {txn.description || '-'}
                    </span>,
                    <span className={`font-semibold ${txn.type === "income" ? "text-emerald-600" : "text-red-500"}`}>
                      {txn.type === "income" ? "+" : "-"}₹{parseFloat(txn.amount).toLocaleString()}
                      {txn.is_recurring && <Icon name="refresh" size={14} className="inline ml-2 text-primary" title={`Recurring: ${txn.recurring_frequency}`} />}
                    </span>,
                    <div>
                      {txn.payment_mode}
                      {txn.reference_number && <div className="text-xs text-slate-400 mt-0.5" title="Reference Number">Ref: {txn.reference_number}</div>}
                    </div>,
                    <Badge color={txn.status === "pending" ? "yellow" : "green"} className="capitalize">
                      {txn.status || 'completed'}
                    </Badge>
                  ];
                  if (showBalance) {
                    row.push(
                      <span className="font-bold text-slate-800 dark:text-slate-200">
                        ₹{parseFloat(txn.running_balance || 0).toLocaleString()}
                      </span>
                    );
                  }
                  row.push(
                    <div className="flex items-center gap-2">
                      <Btn circle xs icon="eye" variant="outline" onClick={() => setViewTxn(txn)} title="View" />
                      <Btn circle xs icon="download" variant="outline" onClick={() => handleDownloadReceipt(txn)} title="Download PDF" />
                      <Btn circle xs icon="content_copy" variant="outline" onClick={() => handleDuplicate(txn)} title="Duplicate" />
                      {hasPermission('festivals.update') && <Btn circle xs icon="edit" onClick={() => handleEdit(txn)} title={t("btn_edit")} />}
                      {hasPermission('festivals.delete') && <Btn circle xs icon="trash" variant="danger" onClick={() => handleDelete(txn.id)} title={t("btn_delete")} />}
                    </div>
                  );
                  return row;
                })}
                footer={
                  <Pagination
                    page={meta.current}
                    total={meta.total}
                    perPage={meta.perPage}
                    onPage={setPage}
                  />
                }
              />
            </div>
          );
        })()}

        {/* Mobile View (Cards) */}
        <div className="block lg:hidden space-y-3 mt-4">
          {txns.length === 0 && !loading && (
            <div className="text-center py-10 text-slate-400 text-sm">
              No transactions found.
            </div>
          )}
          {txns.map((txn, i) => (
            <IonItemSliding key={txn.id} className={`rounded-xl border border-slate-100 dark:border-slate-800 shadow-sm overflow-hidden ${i === 0 ? 'animate-swipe-hint' : ''}`} style={{ '--background': 'transparent' }}>
              <IonItem lines="none" className="ion-no-padding w-full" style={{ '--background': 'transparent', '--padding-start': '0px', '--padding-end': '0px', '--inner-padding-start': '0px', '--inner-padding-end': '0px', '--min-height': '0px' }}>
                <div className="flex w-full items-start gap-3 p-3 sm:p-3.5 bg-white dark:bg-slate-900 box-border">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-0.5 ${txn.type === "income" ? "gradient-success" : "gradient-danger"}`}>
                    {txn.is_recurring ? <Icon name="refresh" size={16} color="white" /> : (txn.type === "income" ? <Icon name="trend_up" size={16} color="white" /> : <Icon name="trend_down" size={16} color="white" />)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start mb-2 gap-2">
                      <div className="flex flex-col min-w-0 flex-1">
                        <div className="text-xs font-mono text-slate-500 mb-0.5 truncate">{txn.txn_id || '-'}</div>
                        <div className="text-sm font-bold text-slate-800 dark:text-slate-100 truncate">{txn.category}</div>
                        <div className="text-xs text-slate-500">{formatDateByPreference(txn.date)}</div>
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0 max-w-[45%] text-right">
                        <div className={`text-sm sm:text-base font-extrabold break-all flex items-center justify-end gap-1 ${txn.type === "income" ? "text-emerald-600" : "text-red-500"}`}>
                          <span>{txn.type === "income" ? "+" : "-"}₹{parseFloat(txn.amount).toLocaleString('en-IN')}</span>
                          {txn.is_recurring && <Icon name="refresh" size={13} className="text-primary shrink-0" />}
                        </div>
                        <Badge color={txn.status === "pending" ? "yellow" : "green"} size="sm" className="capitalize">
                          {txn.status || 'completed'}
                        </Badge>
                      </div>
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400 mb-3 break-words">{txn.description || t("desc_no_description")}</div>
                    <div className="flex items-center justify-between mt-3 gap-2">
                      <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                        <div className="text-xs font-semibold text-slate-500">{txn.payment_mode}</div>
                        {txn.reference_number && <div className="text-xs text-slate-400 px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 rounded truncate max-w-[130px]">Ref: {txn.reference_number}</div>}
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Btn circle xs icon="visibility" variant="outline" onClick={() => setViewTxn(txn)} title="View"></Btn>
                        <Btn circle xs icon="download" variant="outline" onClick={() => handleDownloadReceipt(txn)} title="Download PDF"></Btn>
                        <Btn circle xs icon="content_copy" variant="outline" onClick={() => handleDuplicate(txn)} title="Duplicate"></Btn>
                      </div>
                    </div>
                  </div>
                </div>
              </IonItem>

              <IonItemOptions side="end">
                {hasPermission('festivals.update') && (
                  <IonItemOption color="primary" onClick={() => handleEdit(txn)}>
                    <div className="flex flex-col items-center justify-center w-12">
                      <Icon name="edit" size={20} color="white" />
                    </div>
                  </IonItemOption>
                )}
                {hasPermission('festivals.delete') && (
                  <IonItemOption color="danger" onClick={() => handleDelete(txn.id)}>
                    <div className="flex flex-col items-center justify-center w-12">
                      <Icon name="trash" size={20} color="white" />
                    </div>
                  </IonItemOption>
                )}
              </IonItemOptions>
            </IonItemSliding>
          ))}
          {meta.total > 0 && (
            <div className="pt-2 pb-4 overflow-x-auto">
              <Pagination
                page={meta.current}
                total={meta.total}
                perPage={meta.perPage}
                onPage={setPage}
              />
            </div>
          )}
        </div>
      </Card>

      {/* FAB for Mobile */}
      {!modal && !festModal && !deleteFestModal && !importModal && !viewTxn && !reportModal.open && hasPermission('festivals.create') && selectedFestivalId && createPortal(
        <div className="fixed bottom-6 right-6 z-50 lg:hidden pointer-events-auto">
          <button
            onClick={() => { setEditingId(null); setModal(true); }}
            className="w-14 h-14 bg-primary text-white rounded-full flex items-center justify-center shadow-[0_8px_30px_rgb(0,0,0,0.12)] hover:scale-105 transition-transform active:scale-95 border-0 cursor-pointer"
          >
            <Icon name="plus" size={24} />
          </button>
        </div>,
        document.body
      )}

    </div>
  );
}
